/**
 * FuelGauge.jsx
 *
 * Vertical fuel tank gauge for the sidebar.
 * Shows fuel reserves with gradient fill that reveals status by level.
 * Click opens fuel purchase screen.
 *
 * Also exports fuel calculation utilities.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// FUEL BURN RATES (gallons per hour by aircraft category)
// ============================================================================
export const FUEL_BURN_RATES = {
  turboprop: 200,
  regional_jet: 800,
  small_narrowbody: 850,
  large_narrowbody: 950,
  small_widebody: 1800,
  large_widebody: 2200,
  heavy: 3200,
  super_heavy: 3500,
};

// Map aircraft types to categories (simplified - expand as needed)
export const AIRCRAFT_FUEL_CATEGORIES = {
  // Turboprops
  'DHC-8-400': 'turboprop',
  'ATR-72': 'turboprop',
  'BN-2': 'turboprop',
  'Caravan': 'turboprop',
  'DHC-2': 'turboprop',
  'DHC-3': 'turboprop',

  // Regional Jets
  'E175': 'regional_jet',
  'E190': 'regional_jet',
  'CRJ-700': 'regional_jet',
  'CRJ-900': 'regional_jet',

  // Small Narrowbody
  '737-700': 'small_narrowbody',
  'A319': 'small_narrowbody',

  // Large Narrowbody
  '737-800': 'large_narrowbody',
  '737-900ER': 'large_narrowbody',
  'A320': 'large_narrowbody',
  'A321': 'large_narrowbody',
  '757-200': 'large_narrowbody',
  '757-300': 'large_narrowbody',

  // Small Widebody
  '767-300': 'small_widebody',
  '787-8': 'small_widebody',
  'A330-200': 'small_widebody',

  // Large Widebody
  '777-200': 'large_widebody',
  '787-10': 'large_widebody',
  'A330-300': 'large_widebody',
  'A350-900': 'large_widebody',

  // Heavy
  '747-400': 'heavy',
  '777-300ER': 'heavy',
  'A350-1000': 'heavy',

  // Super Heavy
  'A380': 'super_heavy',
};

/**
 * Get fuel burn rate for an aircraft type
 */
export function getFuelBurnRate(aircraftType) {
  const category = AIRCRAFT_FUEL_CATEGORIES[aircraftType] || 'large_narrowbody';
  return FUEL_BURN_RATES[category] || 950; // Default to large narrowbody
}

/**
 * Calculate fuel needed for a flight
 * @param {number} distanceNm - Distance in nautical miles
 * @param {string} aircraftType - Aircraft type code
 * @param {number} cruiseSpeed - Cruise speed in knots (optional, uses default)
 * @returns {number} Fuel needed in gallons
 */
export function calculateFuelNeeded(distanceNm, aircraftType, cruiseSpeed = 470) {
  const burnRate = getFuelBurnRate(aircraftType);
  const flightTimeHours = distanceNm / cruiseSpeed;
  const fuelNeeded = Math.ceil(flightTimeHours * burnRate);

  // Add 10% reserve
  return Math.ceil(fuelNeeded * 1.1);
}

/**
 * Format gallons for display
 */
function formatGallons(gallons) {
  if (gallons >= 1000000) {
    return `${(gallons / 1000000).toFixed(1)}M gal`;
  }
  if (gallons >= 1000) {
    return `${Math.round(gallons / 1000)}K gal`;
  }
  return `${gallons.toLocaleString()} gal`;
}

/**
 * Calculate estimated days of fuel remaining
 */
function estimateDaysRemaining(currentFuel, dailyBurnRate) {
  if (!dailyBurnRate || dailyBurnRate <= 0) return null;
  const days = Math.floor(currentFuel / dailyBurnRate);
  return days;
}

/**
 * FuelGauge Component
 *
 * @param {number} currentFuel - Current fuel in gallons
 * @param {number} maxFuel - Maximum fuel capacity in gallons
 * @param {number} dailyBurnRate - Average daily fuel consumption
 * @param {function} onClick - Click handler (optional, defaults to navigate to fuel page)
 */
export function FuelGauge({
  currentFuel = 500000,
  maxFuel = 1000000,
  dailyBurnRate = 50000,
  onClick,
}) {
  const navigate = useNavigate();

  // Calculate fill percentage (0-100)
  const fillPercent = Math.max(0, Math.min(100, (currentFuel / maxFuel) * 100));

  // Calculate days remaining
  const daysRemaining = estimateDaysRemaining(currentFuel, dailyBurnRate);

  // Determine warning state (spec: 20% yellow, 10% red, 0% blocked)
  const isEmpty = fillPercent <= 0;
  const isCritical = fillPercent <= 10 && fillPercent > 0;
  const isLow = fillPercent <= 20 && fillPercent > 10;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate('/fuel');
    }
  };

  // Warning/status label
  const getStatusLabel = () => {
    if (isEmpty) return 'FUEL EMPTY';
    if (isCritical) return 'FUEL CRITICAL';
    if (isLow) return 'FUEL LOW';
    return 'FUEL';
  };

  const getStatusColor = () => {
    if (isEmpty || isCritical) return '#ef4444';
    if (isLow) return '#f59e0b';
    return '#737373';
  };

  return (
    <div
      onClick={handleClick}
      style={{
        padding: '8px',
        backgroundColor: isEmpty ? '#1f0505' : isCritical ? '#1a0a0a' : '#0a0a0a',
        border: `1px solid ${isEmpty ? '#ef4444' : isCritical ? '#ef4444' : isLow ? '#f59e0b' : '#1a1a1a'}`,
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        animation: isEmpty ? 'pulse 1s infinite' : 'none',
      }}
      title={isEmpty ? 'FUEL EMPTY - Cannot dispatch flights! Click to purchase fuel.' : 'Click to purchase fuel'}
    >
      {/* Label */}
      <div style={{
        fontSize: '8px',
        fontWeight: '700',
        color: getStatusColor(),
        letterSpacing: '0.5px',
        marginBottom: '6px',
        textAlign: 'center',
      }}>
        {getStatusLabel()}
      </div>

      {/* Tank Container */}
      <div style={{
        position: 'relative',
        height: '80px',
        width: '100%',
        backgroundColor: '#111',
        borderRadius: '4px',
        overflow: 'hidden',
        border: '1px solid #222',
      }}>
        {/* Gradient Background (always visible, represents what WOULD show if full) */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'linear-gradient(to top, #ef4444 0%, #f97316 20%, #eab308 40%, #22d3ee 60%, #84cc16 80%, #22c55e 100%)',
          opacity: 0.15, // Faded background shows tank shape
        }} />

        {/* Actual Fill Level */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: `${fillPercent}%`,
          background: 'linear-gradient(to top, #ef4444 0%, #f97316 20%, #eab308 40%, #22d3ee 60%, #84cc16 80%, #22c55e 100%)',
          backgroundSize: '100% calc(100% * (100 / ' + fillPercent + '))',
          backgroundPosition: 'bottom',
          transition: 'height 0.5s ease',
          boxShadow: (isEmpty || isCritical)
            ? '0 0 10px rgba(239, 68, 68, 0.5)'
            : isLow
              ? '0 0 8px rgba(249, 115, 22, 0.4)'
              : 'none',
        }} />

        {/* Tank Lines (tick marks) */}
        {[25, 50, 75].map(tick => (
          <div
            key={tick}
            style={{
              position: 'absolute',
              bottom: `${tick}%`,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: '#333',
              opacity: 0.5,
            }}
          />
        ))}

        {/* Percentage Overlay */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '14px',
          fontWeight: '700',
          fontFamily: 'monospace',
          color: fillPercent > 50 ? '#000' : '#fff',
          textShadow: fillPercent > 50
            ? '0 0 4px rgba(255,255,255,0.5)'
            : '0 0 4px rgba(0,0,0,0.8)',
          zIndex: 2,
        }}>
          {Math.round(fillPercent)}%
        </div>
      </div>

      {/* Stats Below Tank */}
      <div style={{
        marginTop: '6px',
        textAlign: 'center',
      }}>
        {/* Gallons */}
        <div style={{
          fontSize: '11px',
          fontWeight: '600',
          fontFamily: 'monospace',
          color: getStatusColor(),
        }}>
          {isEmpty ? 'EMPTY' : formatGallons(currentFuel)}
        </div>

        {/* Days Estimate or Warning Message */}
        {isEmpty ? (
          <div style={{
            fontSize: '9px',
            color: '#ef4444',
            marginTop: '2px',
            fontWeight: '600',
          }}>
            FLIGHTS BLOCKED
          </div>
        ) : daysRemaining !== null && (
          <div style={{
            fontSize: '9px',
            color: getStatusColor(),
            marginTop: '2px',
          }}>
            ~{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

export default FuelGauge;
